import React from 'react';
import { Game, Language } from '../../types';
import { getTeamName } from '../../services/teamService';
import { t } from '../../i18n';
import { Video } from 'lucide-react';

interface GameCardProps {
  game: Game;
  lang: Language;
  onClick?: (game: Game) => void;
}

export const GameCard: React.FC<GameCardProps> = React.memo(({ game, lang, onClick }) => {
  const isLive = game.status === 'Live';
  const isFinished = game.status === 'Finished';

  // 승리 팀 판단 로직
  const scores = game.score ? game.score.split('-').map(s => parseInt(s.trim())) : [0, 0];
  const homeWin = isFinished && scores[0] > scores[1];
  const awayWin = isFinished && scores[1] > scores[0];

  return (
    <div 
      onClick={() => onClick && onClick(game)} 
      className="relative group flex flex-col bg-white overflow-hidden rounded-[28px] border border-slate-100 md:hover:border-rose-200 transition-all cursor-pointer"
    >
      <div className="flex items-center justify-between py-6 px-4 md:px-8">
        {/* Home Team */}
        <div className="flex-1 flex flex-col items-center gap-3 min-w-0">
          <div className={`w-14 h-14 md:w-16 md:h-16 p-3 bg-white rounded-3xl shadow-sm border transition-all duration-500 relative ${homeWin ? 'border-rose-500 ring-4 ring-rose-50' : 'border-slate-100'}`}>
            <img src={game.homeTeam?.logo} alt="" className="w-full h-full object-contain" loading="lazy" />
            {homeWin && (
                <div className="absolute -top-2 -right-2 bg-rose-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                    <span className="text-[10px] font-black italic">W</span>
                </div>
            )}
          </div>
          <span className={`text-xs md:text-sm font-black uppercase text-center w-full truncate leading-tight tracking-tight ${homeWin ? 'text-rose-600' : 'text-slate-900'}`}>
            {getTeamName(game.homeTeam?.id, lang, game.homeTeam?.name)}
          </span>
        </div>
        
        {/* Score / Time Area */}
        <div className="flex flex-col items-center justify-center w-24 md:w-32 shrink-0 mx-2">
          {isLive || isFinished ? (
            <div className="flex flex-col items-center gap-1">
              {isFinished ? (
                <span className="text-[8px] font-black text-rose-500 uppercase tracking-[0.2em] mb-1">{t(lang, 'ftResult')}</span>
              ) : (
                <div className="flex items-center gap-1 mb-1">
                    <span className="text-[8px] font-black text-rose-500 uppercase tracking-[0.2em]">
                      {game.elapsed ? `${game.elapsed}'` : game.currentMinute ? `${game.currentMinute}'` : 'Live'}
                    </span>
                </div>
              )}
              <span className={`text-3xl md:text-4xl font-black tabular-nums tracking-widest drop-shadow-sm ${isLive ? 'text-rose-500' : 'text-slate-900'}`}>
                {game.score || '0-0'}
              </span>
              {isLive && (
                <div className="flex items-center px-2 py-0.5 bg-rose-100 rounded-md animate-pulse">
                    <span className="text-[9px] font-black text-rose-600 tabular-nums">
                      Live
                    </span>
                </div>
              )}
              {/* Highlight Badge */}
              {(game as any).hasHighlight && (
                <div className="mt-2 flex items-center gap-1.5 px-2 py-0.5 bg-rose-50 rounded-full border border-rose-100">
                  <Video size={10} className="text-rose-500 fill-rose-500" />
                  <span className="text-[8px] font-bold text-rose-600 uppercase tracking-tight">Highlight</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">
                {t(lang, 'scheduled')}
              </span>
              <span className="text-2xl md:text-3xl font-black text-slate-900 tabular-nums tracking-wider">
                {game.time}
              </span>
            </div>
          )}
        </div>

        {/* Away Team */}
        <div className="flex-1 flex flex-col items-center gap-3 min-w-0">
          <div className={`w-14 h-14 md:w-16 md:h-16 p-3 bg-white rounded-3xl shadow-sm border transition-all duration-500 relative ${awayWin ? 'border-rose-500 ring-4 ring-rose-50' : 'border-slate-100'}`}>
            <img src={game.awayTeam?.logo} alt="" className="w-full h-full object-contain" loading="lazy" />
            {awayWin && (
                <div className="absolute -top-2 -left-2 bg-rose-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                    <span className="text-[10px] font-black italic">W</span>
                </div>
            )}
          </div>
          <span className={`text-xs md:text-sm font-black uppercase text-center w-full truncate leading-tight tracking-tight ${awayWin ? 'text-rose-600' : 'text-slate-900'}`}>
            {getTeamName(game.awayTeam?.id, lang, game.awayTeam?.name)}
          </span>
        </div>
      </div>

      {/* Card Footer Decoration */}
      <div className="h-1 w-full bg-gradient-to-r from-transparent via-slate-100 to-transparent opacity-50" />
    </div>
  );
});

GameCard.displayName = 'GameCard';
